/**
 * Shared offer-projection helpers. Three call shapes converge on
 * DraftOffer (the data contract PokemonFullSheet renders against):
 *
 *   - snapshotToOffer(PartyMemberSnapshot)  — for the Pokédex's frozen
 *     post-pick records. Snapshot fields are denormalized at pick time,
 *     so no async dex lookup needed.
 *   - partyMemberToOffer(PartyMember)       — for the orb-chrome's tap
 *     path. Member state is fresher than the snapshot (level, HP,
 *     moves-after-level-up); we re-derive display fields from @pkmn/dex.
 *   - careerEntryToOffer(CareerEntry)       — lives in PokedexScene
 *     today; pulled here if it ever needs a second caller.
 *
 * Source-of-truth rule for `partyMemberToOffer`: always derive from the
 * live PartyMember, NEVER from pokedex.drafted snapshots. The snapshot
 * is intentionally immutable per the Pokédex design; the party is what
 * the player is actually holding. Reading the snapshot would surface
 * stale level / HP / moves on every orb tap.
 */
import { loadGen3 } from '../data/dex';
import { SPECIES_TO_DEX } from '../battle/teamFromParty';
import { gameAbilityDesc } from '../data/abilityDescOverrides';
import type {
  PartyMember,
  PartyMemberSnapshot,
} from './runState';
import type { DraftOffer } from './draftRoll';

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * Compose a DraftOffer from a PartyMemberSnapshot. Synchronous because the
 * snapshot already carries every display field denormalized at draft-pick
 * time — no dex roundtrip required. Shallow-copies array + object members
 * so callers can't accidentally mutate the snapshot through the offer.
 *
 * Was previously a private fn in PokedexScene.ts; extracted here so the
 * orb-chrome callers (PartyOrbs + PartySheetScene) can reuse it without
 * importing from a scene file.
 */
export function snapshotToOffer(snap: PartyMemberSnapshot): DraftOffer {
  return {
    id: snap.id,
    species: snap.species,
    displayName: snap.displayName,
    dexId: snap.dexId,
    types: [...snap.types],
    level: snap.level,
    moves: [...snap.moves],
    movesDisplay: [...snap.movesDisplay],
    nature: snap.nature,
    ivs: { ...snap.ivs },
    ability: snap.ability,
    abilityName: snap.abilityName,
    // Read-time remap: persisted snapshots (saves + career) may carry
    // stale Showdown-register descs forever — the stored ability id
    // lets the override win at display time, zero migrations.
    abilityDesc: gameAbilityDesc(snap.ability, snap.abilityDesc),
    baseStats: { ...snap.baseStats },
    eggPullMove: snap.eggPullMove,
    isRarePull: !!snap.eggPullMove,
    gender: snap.gender,
    caughtAt: { ...snap.caughtAt },
  };
}

/**
 * Compose a DraftOffer from a live PartyMember. Async because display
 * fields (species name, types, ability description, move display names,
 * baseStats) come from @pkmn/dex which is lazy-imported. The dex chunk
 * is eager-warmed at boot (main.ts line 31), so the await typically
 * resolves in microseconds with a warm cache; cold cache (~100 ms)
 * surfaces through PartySheetScene's skeleton-then-patch render path.
 *
 * Defensive defaults:
 *   - unknown species   → baseStats zeros (PokemonFullSheet treats 0 as
 *                          "no data" and renders IV-only display)
 *   - missing ability   → empty strings (full-sheet ability row hides)
 *   - missing move data → capitalize(id) as the display fallback
 *
 * `isRarePull` is always false — that's an offer-time flag specific to
 * the draft modal; live members past the pick beat don't carry it
 * forward (they may have an egg-pull move in their moves[] but the
 * UI signal lives only at draft time).
 *
 * Eng review path (l): the snapshot path in PokemonFullSheet's
 * stat-computation branch (line 162's `base > 0` check) now sees real
 * values for party members thanks to this baseStats wiring; the prior
 * regression where party-tab full sheets rendered with stat=IV-only
 * fell out of the snapshot-derived offer not knowing the species.
 */
export async function partyMemberToOffer(member: PartyMember): Promise<DraftOffer> {
  const gen = await loadGen3();
  const sp = gen.species.get(member.species);
  const baseStats = sp?.baseStats
    ? {
        hp: sp.baseStats.hp,
        atk: sp.baseStats.atk,
        def: sp.baseStats.def,
        spa: sp.baseStats.spa,
        spd: sp.baseStats.spd,
        spe: sp.baseStats.spe,
      }
    : { hp: 0, atk: 0, def: 0, spa: 0, spd: 0, spe: 0 };
  const abilityRef = member.ability ? gen.abilities.get(member.ability) : null;
  const moves = member.moves ?? [];
  return {
    id: member.id,
    species: member.species,
    displayName: sp?.name ?? capitalize(member.species),
    dexId: SPECIES_TO_DEX[member.species] ?? 0,
    types: (sp?.types as readonly string[] | undefined)?.slice() ?? [],
    level: member.level,
    moves: [...moves],
    movesDisplay: moves.map((id) => gen.moves.get(id)?.name ?? capitalize(id)),
    nature: member.nature,
    ivs: { ...member.ivs },
    ability: member.ability,
    abilityName: abilityRef?.name ?? '',
    abilityDesc: gameAbilityDesc(member.ability, abilityRef?.shortDesc ?? ''),
    baseStats,
    isRarePull: false,
    gender: member.gender,
    caughtAt: { ...member.caughtAt },
  };
}
